<template>
  <div class="admin-auth">
    <div class="admin-auth__shell">
      <section class="admin-auth__brand">
        <div class="admin-auth__brand-content">
          <img
            :src="logoSrc"
            alt="Adverport"
            class="admin-auth__brand-logo"
          >
          <span class="admin-auth__eyebrow">Affiliate Operations Console</span>
          <h1 class="admin-auth__title">
            Adverport yönetim paneli
          </h1>
          <p class="admin-auth__description">
            Kampanyaları, kullanıcıları ve finans akışlarını tek operasyon yüzeyinden yönetin.
          </p>
        </div>
      </section>

      <section class="admin-auth__panel">
        <v-card
          :elevation="0"
          class="admin-auth__card admin-login-card"
        >
          <v-card-text>
            <v-form
              class="admin-login-card__form"
              @submit.prevent="login()"
            >
              <div class="admin-login-card__mobile-brand">
                <img
                  :src="logoSrc"
                  alt="Adverport"
                  class="admin-auth__brand-logo"
                >
                <span class="admin-auth__eyebrow">Affiliate Operations Console</span>
                <h1 class="admin-login-card__mobile-title">
                  Adverport yönetim paneli
                </h1>
                <p class="admin-login-card__mobile-description">
                  Kampanyaları, kullanıcıları ve finans akışlarını tek operasyon yüzeyinden yönetin.
                </p>
              </div>

              <div class="admin-login-card__heading">
                <div class="admin-login-card__title">
                  Hoş geldiniz
                </div>
                <v-card-subtitle class="pa-0 mt-2">
                  Yönetim paneline erişmek için admin hesabınızla giriş yapın.
                </v-card-subtitle>
              </div>

              <div class="admin-login-card__fields">
                <v-text-field
                  v-model="username"
                  class="admin-login-card__field"
                  dense
                  outlined
                  :rules="[rules.required]"
                  label="Admin E-posta / Kullanıcı Adı"
                  :prepend-inner-icon="icons.mdiEmailOutline"
                  placeholder="admin veya admin@adverport.com"
                  hide-details="auto"
                ></v-text-field>

                <v-text-field
                  v-model="password"
                  class="admin-login-card__field"
                  dense
                  outlined
                  :type="isPasswordVisible ? 'text' : 'password'"
                  label="Şifre"
                  placeholder="••••••••••••"
                  :rules="[rules.required]"
                  :append-icon="isPasswordVisible ? icons.mdiEyeOffOutline : icons.mdiEyeOutline"
                  hide-details="auto"
                  @click:append="isPasswordVisible = !isPasswordVisible"
                  @keyup.enter="login()"
                ></v-text-field>
              </div>

              <v-btn
                type="submit"
                block
                color="primary"
                :loading="isLoading"
                class="admin-login-card__submit"
              >
                Giriş Yap
              </v-btn>
            </v-form>

            <div
              v-if="showErrorResult"
              class="admin-auth__error"
            >
              <v-alert
                dense
                outlined
                type="error"
              >
                {{ errorMessage }}
              </v-alert>
            </div>
          </v-card-text>
        </v-card>
      </section>
    </div>
  </div>
</template>
<script>
import { mdiEmailOutline, mdiEyeOffOutline, mdiEyeOutline } from '@mdi/js'
import { ref } from '@vue/composition-api'
import axios from 'axios'
import {
  clearPanelSession,
  fetchPanelSession,
  setAccessToken,
  setPanelSession,
} from '@/utils/panelSession'
import adverportLogo from '@/assets/images/logos/adverport-logo.png'

export default {
  setup() {
    const isPasswordVisible = ref(false)
    const username = ref('')
    const password = ref('')
    const isLoading = ref(false)
    const showErrorResult = ref(false)
    const errorMessage = ref('Giriş başarısız. Kullanıcı adı veya şifreyi kontrol edin.')

    return {
      isPasswordVisible,
      username,
      password,
      isLoading,
      showErrorResult,
      errorMessage,
      logoSrc: adverportLogo,
      icons: {
        mdiEmailOutline,
        mdiEyeOutline,
        mdiEyeOffOutline,
      },
      rules: {
        required: value => !!value || 'Bu alan zorunludur.',
      },
    }
  },
  methods: {
    async resolvePanelSession(token) {
      return fetchPanelSession(token)
    },
    resolveLoginError(error) {
      if (error?.panelAccessDenied) {
        return 'Bu hesap panel erişimi için yetkili değil.'
      }
      const status = error?.response?.status
      if (status === 401 || status === 403 || status === 404) {
        return 'Panel erişimi bulunamadı veya kullanıcı bilgileri hatalı.'
      }
      if (status >= 500) {
        return 'Local staging backend yanıt vermiyor.'
      }

      return error?.response?.data?.message || error?.message || 'Giriş başarısız. Kullanıcı adı veya şifreyi kontrol edin.'
    },
    async login() {
      if (this.isLoading) return

      const normalizedUsername = (this.username || '').trim()
      const normalizedPassword = this.password || ''

      if (normalizedUsername === '' || normalizedPassword === '') {
        this.showErrorResult = true
        this.errorMessage = 'E-posta ve şifre alanları zorunludur.'

        return
      }

      const payload = new URLSearchParams()
      payload.append('username', normalizedUsername)
      payload.append('password', normalizedPassword)

      this.isLoading = true
      this.showErrorResult = false
      this.errorMessage = 'Giriş başarısız. Kullanıcı adı veya şifreyi kontrol edin.'

      try {
        clearPanelSession()

        const response = await axios.post(`${process.env.VUE_APP_BASE_API_URL}panel/login`, payload, {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          timeout: 12000,
        })
        const token = response.data?.data?.token

        if (!token) {
          throw new Error('Token alınamadı')
        }

        setAccessToken(token)
        const panelSession = await this.resolvePanelSession(token)
        setPanelSession(panelSession)
        this.$router.replace({ name: 'dashboard' })
      } catch (error) {
        clearPanelSession()
        this.showErrorResult = true
        this.errorMessage = this.resolveLoginError(error)
        setTimeout(() => {
          this.showErrorResult = false
        }, 4000)
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>
